import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { Col, Row } from "react-bootstrap";
import { get, uniqBy } from "lodash";

import Loading from "../../../../components/Loading/Loading";
import SelectSides from "../SelectSides/SelectSides";
import SwitchOnKitProcessor from "./SwitchOnKitProcessor";
import SwitchOnKitColours from "./SwitchOnKitColours";
import SwitchOnKitOptions from "./SwitchOnKitOptions";
import SwitchOnKitType from "./SwitchOnKitType";
import { CartStatus, OrderBuilderStatus } from "../../store/constants";
import { OrderStatus } from "../../../storeHome/store/constants";

import(/* webpackChunkName: "order-builder-model" */ "../RICBTEModel/model.scss");
import(/* webpackChunkName: "order-builder-ite-model" */ "../ITEModel/iteModel.scss");
import(/* webpackChunkName: "order-builder-ite-custom-options" */ "../ITECustomOptions/iteCustomOptions.scss");
import(/* webpackChunkName: "order-builder-ite-shell-colour" */ "../ITEShellColour/iteShellColour.scss");
import(/* webpackChunkName: "order-builder-ite-performance-level" */ "../ITEPerformanceLevel/itePerformanceLevel.scss");
import(/* webpackChunkName: "order-builder-switch-on-kit" */ "./switchOnKitItemBuilder.scss");

const COLOUR_CODES = ["AB_PROCESSOR_COLOUR", "AB_HP_CABLE_COLOUR"];
const STANDARD_OPTION_CODES = ["AB_CABLE_LENGTH", "AB_MAGNET_TYPE", "AB_MAGNET_STRENGTH", "AB_BATTERY", "AB_BATTERY_QTY", "AB_EAR_HOOK_TYPE", "AB_EAR_HOOK_SIZE"];

const splitOptions = (value) => (value || "").split("|").filter(Boolean);
const SwitchOnKitItemBuilder = ({
  cartData, cartStatus, addOrderToCart, orderBuilderStatus, orderBuilderData,
  setIsStockOrder, loadCustomOptionsGroup, customOptionsGroupData,
  customOptionsGroupLeft, customOptionsGroupRight,
  loadConfigurationGroup, configurationGroupData,
  configurationGroupLeft, configurationGroupRight,
}) => {
  const history = useHistory();
  const items = get(orderBuilderData, "Items", []);
  const models = get(orderBuilderData, "Models", []);
  const [selectedModelCode, setSelectedModelCode] = useState("");
  const [leftOrRight, setLeftOrRight] = useState("");
  const [linked, setLinked] = useState(true);
  const [customOptions, setCustomOptions] = useState({ R: {}, L: {} });
  const [submittedItems, setSubmittedItems] = useState([]);
  const [errorMessages, setErrorMessages] = useState([]);

  const availableModels = useMemo(() => {
    const codes = items.map((item) => item.ModelTypeCode).filter(Boolean);
    return uniqBy(models.filter((model) => codes.includes(model.Code)), "Code");
  }, [items, models]);
  const selectedModelItems = useMemo(
    () => items.filter((item) => item.ModelTypeCode === selectedModelCode),
    [items, selectedModelCode]
  );
  const selectedSides = leftOrRight === "B" ? ["R", "L"] : leftOrRight ? [leftOrRight] : [];
  const rightSelected = selectedSides.includes("R");
  const leftSelected = selectedSides.includes("L");
  const setSideSelected = (side, selected) => {
    const otherSide = side === "R" ? leftSelected : rightSelected;
    if (selected && otherSide) setLeftOrRight("B");
    else if (selected) setLeftOrRight(side);
    else if (otherSide) setLeftOrRight(side === "R" ? "L" : "R");
    else setLeftOrRight("");
  };
  const getSideItem = (side) => selectedModelItems.find((item) =>
    (item.Code || "").toUpperCase().endsWith(`-${side}`) ||
    (item.Name || "").toLowerCase().includes(side === "R" ? "right" : "left")
  );
  const getOptionsForSide = (side) =>
    (side === "R" ? customOptionsGroupRight : customOptionsGroupLeft) || [];

  // AB_SWITCHKIT is an internal model type used to load the existing builder
  // data and custom-option group. It is not a customer-facing selection.
  useEffect(() => {
    if (!selectedModelCode && availableModels.length) {
      setSelectedModelCode(availableModels[0].Code);
    }
  }, [availableModels, selectedModelCode]);

  useEffect(() => {
    if (selectedModelCode && loadCustomOptionsGroup) {
      loadCustomOptionsGroup("B", selectedModelCode, customOptionsGroupData || {});
      const model = availableModels.find((entry) => entry.Code === selectedModelCode);
      if (model && model.ConfigurationGroupCode && loadConfigurationGroup) {
        loadConfigurationGroup("B", model.ConfigurationGroupCode, configurationGroupData || {});
      }
      setCustomOptions({ R: {}, L: {} });
    }
  }, [selectedModelCode]);

  useEffect(() => {
    if (cartStatus === CartStatus.SUCCESS && submittedItems.length) {
      history.push({
        pathname: `/related-products/${submittedItems[0].Code}`,
        state: { productsAddedToCart: submittedItems },
      });
    }
  }, [cartStatus, history, submittedItems]);

  const optionVisible = (option, side) => {
    const selections = customOptions[side];
    return optionVisibleForSelections(option, selections);
  };

  const optionVisibleForSelections = (option, selections) => {
    if (option.Code === "AB_HP_CABLE_COLOUR") return selections["Speech Processor"] === "Sky CI M90";
    if (option.Code === "AB_MAGNET_STRENGTH") return Boolean(selections["Magnet Type"]);
    if (option.Code === "AB_BATTERY_QTY") return Boolean(selections.Battery);
    if (option.Code === "AB_EAR_HOOK_SIZE") return Boolean(selections["Ear Hook Type"]);
    return true;
  };

  const setOption = (side, option, value) => {
    setCustomOptions((current) => {
      const updateSide = (source) => {
        const nextSide = { ...source, [option.Name]: value };
        if (option.Code === "AB_PROCESSOR") {
          delete nextSide["Speech Processor Colour"];
          if (value !== "Sky CI M90") delete nextSide["Headpiece Cable Colour"];
        }
        if (option.Code === "AB_MAGNET_TYPE") delete nextSide["Magnet Strength"];
        if (option.Code === "AB_BATTERY") delete nextSide["Battery Quantity"];
        if (option.Code === "AB_EAR_HOOK_TYPE") delete nextSide["Ear Hook Size"];
        return nextSide;
      };
      const nextSide = updateSide(current[side]);
      const otherSide = side === "R" ? "L" : "R";
      return linked && rightSelected && leftSelected
        ? { ...current, [side]: nextSide, [otherSide]: updateSide(current[otherSide]) }
        : { ...current, [side]: nextSide };
    });
  };

  const allowedValues = (option, side) => {
    return allowedValuesForSelections(option, customOptions[side]);
  };

  const allowedValuesForSelections = (option, selections) => {
    const values = splitOptions(option.Options);
    if (option.Code === "AB_EAR_HOOK_SIZE") {
      return selections["Ear Hook Type"] === "M T-Mic"
        ? values.filter((value) => ["Small", "Medium", "Large"].includes(value))
        : values.filter((value) => ["0", "1", "2", "3"].includes(value));
    }
    return values;
  };

  useEffect(() => {
    if (!selectedModelCode || (!customOptionsGroupRight && !customOptionsGroupLeft)) return;

    const buildDefaults = (side, current) => {
      const group = getOptionsForSide(side);
      const config = side === "R" ? configurationGroupRight : configurationGroupLeft;
      const next = { ...current };

      // Multiple passes allow a selected processor/type to reveal and populate
      // its dependent option during the same update.
      for (let pass = 0; pass < 3; pass += 1) {
        group.forEach((option) => {
          if (!optionVisibleForSelections(option, next) || next[option.Name]) return;
          if (COLOUR_CODES.includes(option.Code)) {
            const colours = option.Code === "AB_PROCESSOR_COLOUR"
              ? get(config, "ShellColours", []) : get(config, "FaceplateColours", []);
            const configuredOrder = splitOptions(option.Options)
              .map((value) => value.split("-").pop().trim());
            const firstConfiguredColour = configuredOrder.find((code) =>
              colours.some((colour) => colour.Code === code)
            );
            if (firstConfiguredColour) next[option.Name] = firstConfiguredColour;
            return;
          }
          const values = allowedValuesForSelections(option, next);
          if (option.DefaultValue && values.includes(option.DefaultValue)) next[option.Name] = option.DefaultValue;
          else if (values.length) next[option.Name] = values[0];
        });
      }
      return next;
    };

    setCustomOptions((current) => {
      const next = {
        R: buildDefaults("R", current.R),
        L: buildDefaults("L", current.L),
      };
      return JSON.stringify(next) === JSON.stringify(current) ? current : next;
    });
  }, [selectedModelCode, customOptionsGroupRight, customOptionsGroupLeft, configurationGroupRight, configurationGroupLeft, customOptions]);

  const addSwitchOnKitToOrder = () => {
    const errors = [];
    if (!selectedModelCode) errors.push("Please select a Switch on Kit model");
    if (!leftOrRight) errors.push("Please select at least one side");
    selectedSides.forEach((side) => getOptionsForSide(side).forEach((option) => {
      const supported = option.Code === "AB_KIT_TYPE" || option.Code === "AB_PROCESSOR" || COLOUR_CODES.includes(option.Code) || STANDARD_OPTION_CODES.includes(option.Code);
      if (supported && optionVisible(option, side) && !customOptions[side][option.Name]) {
        errors.push(`Please select ${option.Name} for ${side === "R" ? "Right" : "Left"}`);
      }
    }));
    const itemsForCart = selectedSides.map((side) => {
      const item = getSideItem(side);
      if (!item) {
        errors.push(`The selected model does not have a ${side === "R" ? "Right" : "Left"} item configured`);
        return null;
      }
      return { ...item, ItemCode: item.Code, Quantity: 1, Side: side, Type: "Standard", CustomOptions: customOptions[side] };
    }).filter(Boolean);
    setErrorMessages(errors);
    if (errors.length) return;
    const currentCart = cartData ? [...cartData] : [];
    itemsForCart.forEach((item) => {
      const index = currentCart.findIndex((existing) => existing.Code === item.Code && existing.Side === item.Side);
      if (index >= 0) currentCart[index] = { ...currentCart[index], Quantity: Number(currentCart[index].Quantity || 0) + 1 };
      else currentCart.push(item);
    });
    setSubmittedItems(itemsForCart);
    setIsStockOrder(OrderStatus.REGULAR);
    addOrderToCart(currentCart);
  };

  return (
    <div className="order-builder-main-div switch-on-kit-item-builder-main-div">
      {orderBuilderStatus === OrderBuilderStatus.LOADING && <Loading />}
      {orderBuilderStatus === OrderBuilderStatus.ERROR && <div className="alert alert-danger">The Switch on Kit products could not be loaded. Please try again.</div>}
      {orderBuilderStatus === OrderBuilderStatus.SUCCESS && <div>
        <h1>Configure your Switch on Kit</h1>
        <p>Select a kit and configure each required side.</p>
        {!availableModels.length ? <div className="alert alert-info">There are currently no Switch on Kit models available to order.</div> : <div>
          <SelectSides rightSelected={rightSelected} setRightSelectedSides={(selected) => setSideSelected("R", selected)} leftSelected={leftSelected} setLeftSelected={(selected) => setSideSelected("L", selected)} />
          {selectedModelCode && selectedSides.length > 0 && <section className="switch-on-kit-options ite-custom-options-component-main-div order-builder-component">
            <SwitchOnKitType rightSelected={rightSelected} leftSelected={leftSelected} getOptionsForSide={getOptionsForSide} allowedValues={allowedValues} selectedOptions={customOptions} setOption={setOption} />
            <SwitchOnKitProcessor rightSelected={rightSelected} leftSelected={leftSelected} linked={linked} setLinked={setLinked} getOptionsForSide={getOptionsForSide} allowedValues={allowedValues} selectedOptions={customOptions} setOption={setOption} getSideItem={getSideItem} />
            <SwitchOnKitColours rightSelected={rightSelected} leftSelected={leftSelected} linked={linked} setLinked={setLinked} getOptionsForSide={getOptionsForSide} optionVisible={optionVisible} selectedOptions={customOptions} setOption={setOption} configurationGroupRight={configurationGroupRight} configurationGroupLeft={configurationGroupLeft} />
            <SwitchOnKitOptions title="Choose options" codes={STANDARD_OPTION_CODES} rightSelected={rightSelected} leftSelected={leftSelected} linked={linked} setLinked={setLinked} getOptionsForSide={getOptionsForSide} optionVisible={optionVisible} allowedValues={allowedValues} selectedOptions={customOptions} setOption={setOption} />
          </section>}
          {errorMessages.length > 0 && <Row className="order-builder-component-title"><Col xs={12} className="alert alert-danger"><p>Please resolve the following errors</p><ul>{errorMessages.map((message) => <li key={message}>{message}</li>)}</ul></Col></Row>}
          <div className="item-builder-add-to-cart-button order-builder-component"><div className="order-builder-component-title"><button className="btn float-right" type="button" disabled={cartStatus === CartStatus.LOADING} onClick={addSwitchOnKitToOrder}>{cartStatus === CartStatus.LOADING ? "Adding to order..." : "Add to order"}</button></div></div>
        </div>}
      </div>}
    </div>
  );
};

SwitchOnKitItemBuilder.propTypes = {
  cartData: PropTypes.array, cartStatus: PropTypes.string, addOrderToCart: PropTypes.func,
  orderBuilderStatus: PropTypes.string, orderBuilderData: PropTypes.object,
  setIsStockOrder: PropTypes.func, loadCustomOptionsGroup: PropTypes.func,
  customOptionsGroupData: PropTypes.object, customOptionsGroupLeft: PropTypes.array,
  customOptionsGroupRight: PropTypes.array,
  loadConfigurationGroup: PropTypes.func, configurationGroupData: PropTypes.object,
  configurationGroupLeft: PropTypes.object, configurationGroupRight: PropTypes.object,
};

export default SwitchOnKitItemBuilder;
